import React, {useState, useEffect} from 'react';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import {theme} from '../../../../styles';
import activities from './activities';
import {GRANITE_9, GRANITE_7, GRANITE_2} from '../../../../colors';
import fetchJSON from '../../../../utils/fetchJSON';
import useSyncPromise from '../../../../utils/react/useSyncPromise';
import CircularProgress from '@material-ui/core/CircularProgress';
import {uniq} from 'lodash';

interface ActivitySelectionProps {
    setStepCount: SetState<number>;
    setActivitiesList: SetState<string[]>;
    activitiesList: string[];
    subCatList: string[];
    setSubCatList: SetState<string[]>;
}

export default function ActivitySelection({
    setStepCount,
    setActivitiesList,
    activitiesList,
    subCatList,
    setSubCatList,
}: ActivitySelectionProps) {
    const [responsePromise, setResponsePromise] = useState<
        Promise<Response> | undefined
    >();
    const [, , isLoaded] = useSyncPromise(responsePromise, undefined);

    const onSubCatClick = (subCat: string) => {
        const itemPosition = subCatList.indexOf(subCat);
        if (itemPosition > -1) {
            const updatedSubCatList = subCatList.filter(
                (item) => item !== subCat
            );
            setSubCatList(updatedSubCatList);
        } else {
            const updatedSubCatList = [...subCatList, subCat];
            setSubCatList(updatedSubCatList);
        }
    };
    useEffect(() => {
        // If an activities' subcategory is selected, add the activity to activityList
        const listActivities: string[] = [];
        activities.forEach((activity) => {
            activity.subCat.forEach((subCat) => {
                if (subCatList.includes(subCat)) {
                    listActivities.push(activity.id);
                }
            });
        });
        setActivitiesList(uniq(listActivities));
    }, [setActivitiesList, subCatList]);

    function onActivitySubmit() {
        // Save users' selected parent activities
        if (activitiesList.length > 0) {
            setResponsePromise(
                fetchJSON('/api/user/activities/', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        activities: activitiesList,
                    }),
                })
            );
        } else {
            setStepCount((prevState: number) => prevState + 1);
        }
    }

    useEffect(() => {
        // After promise is resolved, move onto the next step in onboarding
        if (responsePromise && isLoaded) {
            setStepCount((prevState: number) => prevState + 1);
        }
    }, [responsePromise, isLoaded, setStepCount]);

    return (
        <div>
            <Typography variant='h5' style={{marginTop: theme.spacing(2)}}>
                How do you explore the outdoors?
            </Typography>
            <Typography variant='body1' style={{margin: theme.spacing(1)}}>
                Pick your top activities.
            </Typography>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    overflow: 'scroll',
                    justifyContent: 'left',
                    height: 315,
                }}
            >
                {activities.map((activity) => (
                    <div key={activity.id} style={{flexBasis: '100%'}}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                color: GRANITE_2,
                            }}
                        >
                            <img
                                src={activity.img}
                                style={{
                                    width: 30,
                                    height: 30,
                                    margin: theme.spacing(1),
                                }}
                            />
                            <Typography variant='h5'>
                                {activity.displayName}
                            </Typography>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                borderTop: '1px solid',
                                borderColor: GRANITE_7,
                                backgroundColor: GRANITE_9,
                            }}
                        >
                            {activity.subCat.map((subCat) => (
                                <Button
                                    key={subCat}
                                    variant={
                                        subCatList.includes(subCat)
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    color='primary'
                                    style={{
                                        fontSize: 14,
                                        margin: theme.spacing(1),
                                    }}
                                    onClick={() => {
                                        onSubCatClick(subCat);
                                    }}
                                >
                                    {subCat}
                                </Button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: theme.spacing(2),
                }}
            >
                <Button
                    style={{fontSize: 14}}
                    onClick={() =>
                        setStepCount((prevState: number) => prevState - 1)
                    }
                >
                    Back
                </Button>
                <Button
                    color='primary'
                    variant='contained'
                    style={{fontSize: 14}}
                    onClick={() => {
                        onActivitySubmit();
                    }}
                >
                    {responsePromise ? (
                        <CircularProgress
                            thickness={7}
                            size={24}
                            disableShrink
                            color='inherit'
                        />
                    ) : (
                        'Next'
                    )}
                </Button>
            </div>
        </div>
    );
}
